import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { FileText, Clock, Upload, Zap, Camera, Trash2, BadgeCheck, AlertCircle, ArrowDown, IndianRupee, TrendingUp, X, ShieldCheck, CheckCircle2, AlertTriangle, Video } from "lucide-react";
import ProfilePhotoEditor from "../components/ProfilePhotoEditor";
import ProfileStrengthCard, { BioEditorModal } from "../components/ProfileStrengthCard";
import TATBadge from "../components/TATBadge";
import DoctorEarnings from "../components/DoctorEarnings";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DoctorDashboard() {
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [cases, setCases] = useState([]);
  const [hideRefunded, setHideRefunded] = useState(true);
  // Format filter chip — "all" | "video" | "written". Lets doctors zero in
  // on Video Consultation cases so they can prep + schedule Zoom slots.
  const [formatFilter, setFormatFilter] = useState("all");
  const [fee, setFee] = useState(user?.consultation_fee || 1000);
  const [licenseBusy, setLicenseBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoBuster, setPhotoBuster] = useState(() => Date.now());
  const [pickedPhoto, setPickedPhoto] = useState(null); // File object when user picks one
  const [introBusy, setIntroBusy] = useState(false);
  const [introBuster, setIntroBuster] = useState(() => Date.now());
  const [editorOpen, setEditorOpen] = useState(false);
  const [bioOpen, setBioOpen] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [rejectFor, setRejectFor] = useState(null); // case object being rejected
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [regNumInput, setRegNumInput] = useState(user?.registration_number || "");
  const [regNumBusy, setRegNumBusy] = useState(false);
  // Local profile mirror — used by the availability toggle so we can flip
  // it instantly without waiting for a full auth-context refresh. Seeded
  // from the auth user and kept in sync when user changes.
  const [profile, setProfile] = useState(user || null);
  useEffect(() => { setProfile(user || null); }, [user]);

  const photoSrc = user?.photo_url ? `${BACKEND}${user.photo_url}&cb=${photoBuster}` : null;
  const introSrc = user?.intro_video_url
    ? `${BACKEND}${user.intro_video_url}${user.intro_video_url.includes("?") ? "&" : "?"}cb=${introBuster}`
    : null;

  const uploadIntroVideo = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^video\//.test(f.type || "")) {
      toast.error("Please pick a video file (MP4, MOV or WebM).");
      return;
    }
    if (f.size > 30 * 1024 * 1024) {
      toast.error("Max size is 30 MB — try trimming to 60s or 720p.");
      return;
    }
    (async () => {
      setIntroBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/doctors/me/intro-video", fd, { headers: { "Content-Type": "multipart/form-data" }});
        const updated = { ...user, intro_video_url: r.data.intro_video_url };
        localStorage.setItem("user", JSON.stringify(updated));
        setIntroBuster(Date.now());
        window.location.reload();  // simplest way to hydrate auth ctx
        toast.success("Intro video uploaded");
      } catch (err) {
        toast.error(fmtErr(err.response?.data?.detail) || "Upload failed");
      } finally { setIntroBusy(false); }
    })();
  };

  const removeIntroVideo = async () => {
    if (!user?.intro_video_url) return;
    try {
      await api.delete("/doctors/me/intro-video");
      const updated = { ...user, intro_video_url: null };
      localStorage.setItem("user", JSON.stringify(updated));
      window.location.reload();
      toast.success("Intro video removed");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Delete failed");
    }
  };

  const load = async () => {
    const c = await api.get("/consultations");
    setCases(c.data);
    try {
      const e = await api.get("/doctors/me/earnings");
      setEarnings(e.data);
    } catch (err) { console.warn("[earnings]", err?.message || err); }
  };
  useEffect(() => { load(); }, []);

  const updateFee = async () => {
    try { await api.put("/doctors/me/fee", { consultation_fee: Number(fee) }); toast.success("Fee updated"); }
    catch { toast.error("Failed"); }
  };

  const verifyRegistration = async () => {
    setVerifyBusy(true);
    try {
      const r = await api.post("/doctors/me/registration/verify");
      setVerifyResult(r.data);
      if (r.data.verified) toast.success("Registration auto-verified");
      else if (r.data.valid_format) toast.message("Format OK — flagged for admin review");
      else toast.error("Registration format invalid");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify");
    } finally { setVerifyBusy(false); }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    if (rejectReason.trim().length < 10) {
      toast.error("Please provide a rejection reason (min 10 chars).");
      return;
    }
    setRejecting(true);
    try {
      const r = await api.post(`/consultations/${rejectFor.id}/reject`, { reason: rejectReason.trim() });
      if (r.data.refund_status === "refunded") {
        toast.success(`Case rejected · ₹${r.data.amount} refunded to patient`);
      } else if (r.data.refund_status === "not_required") {
        toast.success("Case rejected (no payment to refund)");
      } else {
        toast.message("Case rejected — refund will be retried");
      }
      setRejectFor(null);
      setRejectReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reject case");
    } finally { setRejecting(false); }
  };

  const uploadLicense = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setLicenseBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("purpose", "license");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const fd2 = new FormData(); fd2.append("file_id", r.data.id);
      await api.put("/doctors/me/license", fd2, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("License uploaded — admin will verify shortly");
      window.dispatchEvent(new Event("noi:profile-refresh"));
    } catch { toast.error("Upload failed"); }
    finally { setLicenseBusy(false); }
  };

  const saveRegistrationNumber = async () => {
    const v = (regNumInput || "").trim();
    if (v.length < 3) { toast.error("Please enter a valid registration number"); return; }
    setRegNumBusy(true);
    try {
      const fd = new FormData(); fd.append("registration_number", v);
      const r = await api.put("/doctors/me/registration-number", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setUser({ ...(user || {}), registration_number: r.data.registration_number });
      toast.success("Registration number saved — admin will review and approve");
      window.dispatchEvent(new Event("noi:profile-refresh"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setRegNumBusy(false); }
  };

  const uploadPhoto = (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please select an image (JPG / PNG / WEBP)."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Source photo too large (max 10 MB before crop)."); return; }
    // Open the crop+face-detection editor; actual upload happens on Apply
    setPickedPhoto(f);
    setEditorOpen(true);
    e.target.value = "";
  };

  const applyCroppedPhoto = async (blob) => {
    setEditorOpen(false);
    setPickedPhoto(null);
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "profile.jpg", { type: "image/jpeg" }));
      const r = await api.post("/doctors/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" }});
      const updated = { ...user, photo_url: r.data.photo_url };
      setUser(updated);
      try { localStorage.setItem("noi_user", JSON.stringify(updated)); } catch (err) { console.warn("[localStorage user]", err?.message || err); }
      setPhotoBuster(Date.now());
      toast.success("Profile photo updated");
      window.dispatchEvent(new Event("noi:profile-refresh"));
    } catch (err) { toast.error(err.response?.data?.detail || "Photo upload failed"); }
    finally { setPhotoBusy(false); }
  };

  const removePhoto = async () => {
    if (!user?.photo_url) return;
    try {
      await api.delete("/doctors/me/photo");
      const updated = { ...user, photo_url: null };
      setUser(updated);
      try { localStorage.setItem("noi_user", JSON.stringify(updated)); } catch (err) { console.warn("[localStorage user]", err?.message || err); }
      toast.success("Photo removed");
    } catch { toast.error("Could not remove photo"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-700">{t("doctor")}</div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900 mt-1">Welcome, {user?.name?.startsWith("Dr.") ? user.name : `Dr. ${user?.name || ""}`}</h1>
            <p className="text-sm text-slate-500">{user?.specialization} · {user?.experience_years} yrs experience</p>
          </div>
        </div>

        {/* Profile completeness meter — covers photo, bio, fee, qualifications
            & intro video. Auto-hides at 100%. */}
        <ProfileStrengthCard onOpenBio={() => setBioOpen(true)} />

        {/* Missing-photo nudge banner */}
        {!user?.photo_url && (
          <div
            className="mt-6 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 sm:px-5 sm:py-4 flex items-start gap-3 shadow-sm"
            data-testid="doctor-photo-nudge-banner"
            role="alert"
          >
            <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-amber-900 text-sm sm:text-base">
                Add your profile photo
              </div>
              <p className="text-xs sm:text-sm text-amber-800/90 mt-0.5 leading-relaxed">
                Doctors with a professional headshot get noticeably more bookings.
                Scroll down to the <span className="font-semibold">Profile photo</span> card below to upload.
              </p>
            </div>
            <a
              href="#doctor-photo-card"
              className="shrink-0 hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium"
              data-testid="doctor-photo-nudge-cta"
            >
              <ArrowDown className="w-4 h-4" /> Upload now
            </a>
          </div>
        )}

        {/* Profile photo card */}
        <Card id="doctor-photo-card" className="mt-6 p-5 sm:p-6 border-slate-200 scroll-mt-24" data-testid="doctor-photo-card">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="relative shrink-0">
              {photoSrc ? (
                <img src={photoSrc} alt={user?.name}
                  className="w-24 h-24 rounded-2xl object-cover ring-2 ring-blue-100 bg-slate-100"
                  data-testid="doctor-photo-preview" />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 grid place-items-center ring-2 ring-blue-100 text-blue-700 text-3xl font-bold font-heading"
                     data-testid="doctor-photo-placeholder">
                  {(user?.name || "D").replace(/^Dr\.?\s*/i, "").trim().slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-heading font-semibold text-slate-900 text-lg flex items-center gap-1.5">
                Profile photo <BadgeCheck className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Add a professional head-shot. Patients see this when picking a specialist — it builds trust and increases bookings.
                Square images ≥ 400×400 px work best. JPG / PNG / WEBP, up to 5 MB.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-blue-700 hover:bg-blue-800 text-white cursor-pointer text-sm font-medium" data-testid="doctor-photo-upload-label">
                  <Camera className="w-4 h-4" />
                  {photoBusy ? "Uploading…" : (user?.photo_url ? "Replace photo" : "Upload photo")}
                  <input type="file" hidden onChange={uploadPhoto} accept="image/*" disabled={photoBusy}
                         data-testid="doctor-photo-upload-input" />
                </label>
                {user?.photo_url && (
                  <Button type="button" variant="outline" onClick={removePhoto}
                          className="h-10 border-red-200 text-red-700 hover:bg-red-50"
                          data-testid="doctor-photo-remove-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Intro video card — 30-60s self-introduction. Renders on the public
            doctor profile hero once uploaded. Uses object storage backend. */}
        <Card id="doctor-intro-video-card" className="mt-6 p-5 sm:p-6 border-slate-200 scroll-mt-24" data-testid="doctor-intro-video-card">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="relative shrink-0">
              {introSrc ? (
                <video
                  src={introSrc}
                  controls
                  playsInline
                  preload="metadata"
                  poster={photoSrc || undefined}
                  className="w-40 h-24 rounded-xl object-cover ring-2 ring-violet-100 bg-slate-900 shadow"
                  data-testid="doctor-intro-preview"
                />
              ) : (
                <div className="w-40 h-24 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 grid place-items-center ring-2 ring-violet-100 text-violet-700"
                     data-testid="doctor-intro-placeholder">
                  <Video className="w-8 h-8"/>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-heading font-semibold text-slate-900 text-lg flex items-center gap-1.5">
                Intro video <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">Optional · 3× more bookings</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Record a 30-60 second clip introducing yourself, your specialty and how you approach a second opinion.
                Plays right on your public profile — patients trust doctors they&apos;ve seen speak.
                MP4 / MOV / WebM, up to 30 MB.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-violet-600 hover:bg-violet-700 text-white cursor-pointer text-sm font-medium" data-testid="doctor-intro-upload-label">
                  <Video className="w-4 h-4" />
                  {introBusy ? "Uploading…" : (user?.intro_video_url ? "Replace video" : "Upload intro video")}
                  <input type="file" hidden onChange={uploadIntroVideo} accept="video/*" disabled={introBusy}
                         data-testid="doctor-intro-upload-input" />
                </label>
                {user?.intro_video_url && (
                  <Button type="button" variant="outline" onClick={removeIntroVideo}
                          className="h-10 border-red-200 text-red-700 hover:bg-red-50"
                          data-testid="doctor-intro-remove-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          {/* Availability toggle — controls WhatsApp bot auto-assignment */}
          <Card className="p-6 border-slate-200 lg:col-span-3">
            <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
              <div>
                <div className="font-heading font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${(profile?.is_available !== false) ? "bg-emerald-500" : "bg-slate-400"}`}></span>
                  {(profile?.is_available !== false) ? "You're available now" : "You're marked unavailable"}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  When ON, new WhatsApp bot cases can auto-assign to you.
                  When OFF, cases route to other available doctors — you can still take manually-assigned cases from admin.
                </div>
              </div>
              <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  {(profile?.is_available !== false) ? "AVAILABLE" : "UNAVAILABLE"}
                </span>
                <input
                  type="checkbox"
                  data-testid="doctor-availability-toggle"
                  className="sr-only peer"
                  checked={profile?.is_available !== false}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    try {
                      await api.put("/doctors/me/availability", { is_available: next });
                      setProfile((p) => ({ ...(p || {}), is_available: next }));
                      toast.success(next ? "🟢 You're online — new cases can be assigned to you" : "⏸ Marked unavailable — new cases will skip you");
                    } catch (err) {
                      toast.error("Could not update availability");
                    }
                  }}
                />
                <span className="w-12 h-6 bg-slate-300 rounded-full relative transition peer-checked:bg-emerald-500 after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:bg-white after:rounded-full after:transition peer-checked:after:translate-x-6"></span>
              </label>
            </div>
          </Card>

          {/* Video slot blocking — doctors mark themselves unavailable for
              specific IST windows (OR / leave / focus time) so those slots
              never appear on the patient slot picker. */}
          <VideoSlotBlockingCard />

          <Card id="doctor-fee-card" className="p-6 border-slate-200 scroll-mt-24">
            <div className="font-heading font-semibold text-slate-900 text-lg">Platform pricing</div>
            <p className="text-xs text-slate-500 mt-1">Country-tiered — set platform-wide, no action needed.</p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-1">India (Cashfree · INR)</div>
              <div className="flex justify-between"><span className="text-slate-500">Regular</span><span className="font-semibold text-slate-900">₹1,000</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Priority / Video</span><span className="font-semibold text-slate-900">₹2,000</span></div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-3">Africa (PayPal · USD)</div>
              <div className="flex justify-between"><span className="text-slate-500">Regular</span><span className="font-semibold text-slate-900">$50 USD</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Priority / Video</span><span className="font-semibold text-slate-900">$100 USD</span></div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-3">Other countries (PayPal · USD)</div>
              <div className="flex justify-between"><span className="text-slate-500">Regular</span><span className="font-semibold text-slate-900">$100 USD</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Priority / Video</span><span className="font-semibold text-slate-900">$150 USD</span></div>
            </div>
          </Card>

          <Card id="doctor-license-card" className="p-6 border-slate-200 scroll-mt-24">
            <div className="font-heading font-semibold text-slate-900 text-lg">{t("license")}</div>
            <p className="text-xs text-slate-500 mt-1">Medical council registration certificate (PDF/JPG).</p>
            <label className="mt-4 inline-flex items-center gap-2 px-4 h-11 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
              <Upload className="w-4 h-4"/> {licenseBusy ? "Uploading…" : (user?.license_file_id ? "Replace license" : "Upload license")}
              <input type="file" hidden onChange={uploadLicense} accept=".pdf,.jpg,.jpeg,.png" data-testid="license-upload-input"/>
            </label>
            {user?.license_file_id && (
              <div className="mt-2 text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3"/> License uploaded — pending admin review
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-slate-100">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">MCI / NMC Registration Number</label>
              <p className="text-[11px] text-slate-500 mt-0.5">Enter exactly as on your license certificate (e.g. <span className="font-mono">TSMC-2010-4421</span>).</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={regNumInput}
                  onChange={(e) => setRegNumInput(e.target.value.toUpperCase())}
                  placeholder="TSMC-XXXX-XXXX"
                  className="flex-1 min-w-[180px] h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="reg-number-input"
                  maxLength={80}
                />
                <Button
                  size="sm"
                  onClick={saveRegistrationNumber}
                  disabled={regNumBusy || !regNumInput.trim() || regNumInput.trim() === (user?.registration_number || "")}
                  className="h-10 bg-blue-700 hover:bg-blue-800 text-white"
                  data-testid="save-reg-number-btn"
                >
                  {regNumBusy ? "Saving…" : "Save"}
                </Button>
              </div>
              {user?.registration_number && (
                <div className="mt-2 text-[11px] text-slate-600">
                  Saved: <span className="font-mono font-semibold text-slate-900">{user.registration_number}</span>
                  {user?.registration_verified
                    ? <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="w-3 h-3"/> Verified by admin</span>
                    : <span className="ml-2 inline-flex items-center gap-1 text-amber-700 font-semibold"><Clock className="w-3 h-3"/> Pending admin review</span>}
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6 border-slate-200">
            <div className="font-heading font-semibold text-slate-900 text-lg">Status</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Cases</span><span className="font-semibold text-slate-900">{cases.length}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Pending</span><span className="font-semibold text-slate-900">{cases.filter(c=>c.status==="awaiting_opinion").length}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Completed</span><span className="font-semibold text-slate-900">{cases.filter(c=>c.status==="completed").length}</span></div>
            </div>
          </Card>
        </div>

        {/* ----------------- EARNINGS ----------------- */}
        <Card id="doctor-earnings-card" className="mt-8 p-5 sm:p-6 border-slate-200 scroll-mt-24" data-testid="doctor-earnings-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-700 inline-flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3"/> Earnings
              </div>
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-1">Your payouts</h2>
              <p className="text-xs text-slate-500 mt-0.5">You earn 70% of every consultation. Settled monthly via NEFT.</p>
            </div>
          </div>
          {/* KPI tiles */}
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-4 shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-100">Lifetime</div>
              <div className="font-heading text-2xl font-bold mt-1 tabular-nums" data-testid="earnings-lifetime">
                ₹{Math.round(earnings?.summary?.lifetime_doctor_share || 0).toLocaleString()}
              </div>
              <div className="text-[10px] text-emerald-100/90 mt-1">{earnings?.summary?.lifetime_completed || 0} completed</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">This month</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="earnings-this-month">
                ₹{Math.round(earnings?.summary?.this_month?.doctor_share || 0).toLocaleString()}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">{earnings?.summary?.this_month?.cases || 0} cases</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Pending payout</div>
              <div className="font-heading text-2xl font-bold text-amber-900 mt-1 tabular-nums" data-testid="earnings-pending">
                ₹{Math.round(earnings?.summary?.pending_payout || 0).toLocaleString()}
              </div>
              <div className="text-[10px] text-amber-800/80 mt-1">Awaiting next NEFT cycle</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Settled</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="earnings-settled">
                ₹{Math.round(earnings?.summary?.settled_total || 0).toLocaleString()}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Already paid</div>
            </div>
          </div>

          {/* Performance metrics — last 90 days */}
          {earnings?.performance && (
            <div className="mt-3 grid grid-cols-3 gap-3" data-testid="doctor-perf-strip">
              <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Avg TAT (90d)</div>
                <div className="font-heading text-xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="perf-avg-tat">
                  {earnings.performance.avg_tat_hours != null ? `${earnings.performance.avg_tat_hours}h` : "—"}
                </div>
                <div className="text-[10px] text-blue-700/80">across {earnings.performance.completed_90d || 0} cases</div>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">On-time rate</div>
                <div className="font-heading text-xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="perf-on-time">
                  {earnings.performance.on_time_pct != null ? `${earnings.performance.on_time_pct}%` : "—"}
                </div>
                <div className="text-[10px] text-emerald-700/80">SLA delivered</div>
              </div>
              <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">Fastest TAT</div>
                <div className="font-heading text-xl font-bold text-slate-900 mt-1 tabular-nums" data-testid="perf-fastest">
                  {earnings.performance.fastest_tat_hours != null ? `${earnings.performance.fastest_tat_hours}h` : "—"}
                </div>
                <div className="text-[10px] text-violet-700/80">your record</div>
              </div>
            </div>
          )}

          {/* Monthly table */}
          {earnings?.monthly?.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm" data-testid="earnings-monthly-table">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3">Month</th>
                    <th className="py-2 pr-3 text-right">Cases</th>
                    <th className="py-2 pr-3 text-right">Completed</th>
                    <th className="py-2 pr-3 text-right">Gross</th>
                    <th className="py-2 pr-3 text-right">Your share (70%)</th>
                    <th className="py-2 pr-3 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {earnings.monthly.map((m) => (
                    <tr key={m.month} className="border-b border-slate-100" data-testid={`earnings-row-${m.month}`}>
                      <td className="py-2.5 pr-3 font-semibold text-slate-900">{m.month}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{m.cases}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-emerald-700">{m.completed}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">₹{Math.round(m.gross).toLocaleString()}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-emerald-700 font-semibold">₹{Math.round(m.doctor_share).toLocaleString()}</td>
                      <td className="py-2.5 pr-3 text-right">
                        {m.settled
                          ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700"><CheckCircle2 className="w-3 h-3"/>Paid</span>
                          : <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700"><Clock className="w-3 h-3"/>Pending</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(!earnings || earnings.monthly?.length === 0) && (
            <div className="mt-5 text-center text-sm text-slate-500 py-6 border border-dashed border-slate-200 rounded-xl">
              No earnings yet. Cases assigned to you will show up here once paid.
            </div>
          )}
        </Card>

        {/* ----------------- MCI/NMC verification ----------------- */}
        <Card id="doctor-registration-card" className="mt-8 p-5 sm:p-6 border-slate-200 scroll-mt-24" data-testid="doctor-registration-card">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="shrink-0 w-10 h-10 rounded-lg bg-blue-50 text-blue-700 grid place-items-center">
              <ShieldCheck className="w-5 h-5"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-slate-900 text-lg flex items-center gap-2">
                MCI / NMC Registration
                {user?.registration_verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="registration-verified-badge">
                    <CheckCircle2 className="w-3 h-3"/> Verified
                  </span>
                )}
                {!user?.registration_verified && user?.registration_status === "needs_review" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    <AlertTriangle className="w-3 h-3"/> Under review
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Reg #: <span className="font-mono font-semibold text-slate-900">{user?.registration_number || "—"}</span>
                {verifyResult?.council_name && <> · Council: <span className="font-semibold text-slate-700">{verifyResult.council_name}</span></>}
              </p>
              {verifyResult && (
                <div className={`mt-2 text-xs ${verifyResult.verified ? "text-emerald-700" : verifyResult.valid_format ? "text-amber-700" : "text-rose-600"}`} data-testid="registration-verify-message">
                  {verifyResult.message}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button onClick={verifyRegistration} disabled={verifyBusy || !user?.registration_number}
                        className="h-9 bg-blue-700 hover:bg-blue-800 text-white text-sm" data-testid="verify-registration-btn">
                  <ShieldCheck className="w-4 h-4 mr-1"/> {verifyBusy ? "Verifying…" : "Auto-verify now"}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Earnings dashboard (Feb 2026) */}
        <DoctorEarnings/>

        <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-8">Assigned cases <span className="text-sm text-slate-500 font-normal">({cases.filter(c => (!hideRefunded || (c.status !== "refunded" && c.status !== "rejected")) && (formatFilter === "all" || c.consultation_format === formatFilter)).length}{hideRefunded ? ` of ${cases.length}` : ""})</span></h2>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none mt-8" data-testid="doctor-hide-refunded-toggle">
            <input type="checkbox" checked={hideRefunded} onChange={(e) => setHideRefunded(e.target.checked)} className="rounded"/>
            Hide refunded/rejected
          </label>
        </div>

        {/* Format filter chips — "🎥 Video only" is the money chip because
            those cases need proactive scheduling / slot prep. */}
        <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="doctor-format-filter">
          {[
            { key: "all",     label: "All",             count: cases.length },
            { key: "video",   label: "🎥 Video only",   count: cases.filter(c => c.consultation_format === "video").length },
            { key: "written", label: "📝 Written only", count: cases.filter(c => c.consultation_format === "written").length },
          ].map(chip => (
            <button
              key={chip.key}
              type="button"
              onClick={() => setFormatFilter(chip.key)}
              data-testid={`doc-filter-${chip.key}`}
              className={
                "text-xs font-medium px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 " +
                (formatFilter === chip.key
                  ? (chip.key === "video"
                      ? "bg-violet-700 border-violet-700 text-white"
                      : "bg-slate-900 border-slate-900 text-white")
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-300")
              }
            >
              <span>{chip.label}</span>
              <span className={
                "text-[10px] font-bold rounded-full px-1.5 " +
                (formatFilter === chip.key ? "bg-white/25 text-white" : "bg-slate-100 text-slate-600")
              }>{chip.count}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {cases.length === 0 && <div className="text-sm text-slate-500">No paid cases yet.</div>}
          {cases.filter(c => (!hideRefunded || (c.status !== "refunded" && c.status !== "rejected")) && (formatFilter === "all" || c.consultation_format === formatFilter)).map(c => (
            <Card key={c.id} className="p-5 border-slate-200 lift" data-testid={`doc-case-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-heading font-semibold text-slate-900 flex items-center gap-2 min-w-0 flex-wrap">
                  {c.priority === "urgent" && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid="doc-urgent-badge"><Zap className="w-3 h-3"/>Emergency</span>}
                  {c.consultation_format === "video" && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200" data-testid="doc-video-badge">🎥 Video</span>}
                  {c.source === "whatsapp_bot_v3" && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title={c.wa_bot_case_id || "WhatsApp bot booking"} data-testid={`doc-wa-badge-${c.id}`}>🤖 WhatsApp</span>}
                  <span className="truncate">{c.patient_name}</span>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${c.status==="completed"?"bg-emerald-50 text-emerald-700 border-emerald-200":c.status==="under_review"?"bg-blue-50 text-blue-700 border-blue-200":"bg-sky-50 text-sky-700 border-sky-200"}`}>
                  {c.status==="completed"?"Report sent":c.status==="under_review"?"Under review":"Awaiting review"}
                </span>
              </div>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{c.symptoms}</p>
              <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-slate-500">
                <TATBadge createdAt={c.created_at} slaHours={c.sla_hours || (c.priority === "urgent" ? 12 : c.priority === "video" ? 24 : 48)} status={c.status} completedAt={c.completed_at}/>
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5"/>{new Date(c.created_at).toLocaleString()}</span>
                <span>· ₹{c.fee_inr || c.fee_rupees || c.fee_after_discount || c.fee || 0}</span>
                <span>· {(c.file_ids?.length || 0) + (c.wa_uploaded_urls?.length || 0)} file(s)</span>
                {c.wa_bot_case_id && <span className="font-mono text-slate-400">· {c.wa_bot_case_id}</span>}
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Link to={`/case/${c.id}`}><Button size="sm" className="bg-blue-700 hover:bg-blue-800" data-testid={`open-case-${c.id}`}>
                  <FileText className="w-4 h-4 mr-1"/> Open case
                </Button></Link>
                {c.status !== "completed" && c.status !== "rejected" && c.status !== "refunded" && (
                  <Button size="sm" variant="outline"
                          onClick={() => setRejectFor(c)}
                          className="h-9 border-rose-200 text-rose-700 hover:bg-rose-50"
                          data-testid={`reject-case-${c.id}`}>
                    <X className="w-4 h-4 mr-1"/> Reject &amp; refund
                  </Button>
                )}
                {(c.status === "rejected" || c.status === "refunded") && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                    Refunded
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <ProfilePhotoEditor
        file={pickedPhoto}
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setPickedPhoto(null); }}
        onApply={applyCroppedPhoto}
      />
      <BioEditorModal
        open={bioOpen}
        initialBio={user?.bio || ""}
        onClose={() => setBioOpen(false)}
        onSaved={(bio) => {
          const updated = { ...user, bio };
          setUser(updated);
          try { localStorage.setItem("noi_user", JSON.stringify(updated)); }
          catch (err) { console.warn("[localStorage user]", err?.message || err); }
        }}
      />

      {/* Reject + auto-refund dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => { if (!o) { setRejectFor(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="reject-case-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertCircle className="w-5 h-5"/> Reject &amp; auto-refund
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Refunds the patient instantly via Cashfree and notifies them with your reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-700 leading-relaxed">
              You are about to <b>decline</b> this case for <span className="font-semibold text-slate-900">{rejectFor?.patient_name}</span>.
              The patient will be <b>refunded ₹{rejectFor?.fee_after_discount || rejectFor?.fee || 0}</b> instantly via Cashfree.
            </p>
            <div>
              <Label htmlFor="reject-reason">Reason (visible to patient)</Label>
              <Textarea id="reject-reason" value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="e.g., Insufficient scan quality. Please re-upload high-resolution MRI."
                        className="mt-1 min-h-[100px]"
                        data-testid="reject-reason-input" />
              <div className="text-[10px] text-slate-500 mt-1">{rejectReason.length}/500 · min 10 characters</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectFor(null); setRejectReason(""); }}
                    data-testid="reject-cancel-btn">Cancel</Button>
            <Button onClick={submitReject} disabled={rejecting || rejectReason.trim().length < 10}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="reject-confirm-btn">
              {rejecting ? "Refunding…" : "Reject & refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Video Slot Blocking — doctors reserve their own unavailable IST hours so
 * the patient slot picker never offers them. Shows the next 7 days × 3
 * slots as toggle chips; clicked chips flip locally and PUT the delta to
 * `/doctors/me/blocked-slots`.
 */
function VideoSlotBlockingCard() {
  const [blocked, setBlocked] = useState(new Set());
  const [busy, setBusy] = useState(null);

  // Generate the same 7×3 slots the patient picker uses, in the same IST tz.
  const buildSlots = () => {
    const ist_offset = 5.5 * 60 * 60 * 1000;
    const nowUtc = new Date();
    const nowIst = new Date(nowUtc.getTime() + ist_offset);
    const out = [];
    for (let day = 0; day < 7; day++) {
      const base = new Date(nowIst);
      base.setDate(base.getDate() + day);
      base.setHours(0, 0, 0, 0);
      const daylabel = base.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
      const slotsForDay = [];
      for (const hh of [10, 14, 18]) {
        const slotIst = new Date(base);
        slotIst.setHours(hh);
        const slotUtc = new Date(slotIst.getTime() - ist_offset);
        if (slotUtc <= nowUtc) continue;
        const iso = slotUtc.toISOString().replace(".000Z", "Z");
        slotsForDay.push({ slot_at: iso, hh });
      }
      if (slotsForDay.length) out.push({ day: daylabel, list: slotsForDay });
    }
    return out;
  };
  const [slots] = useState(buildSlots);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/doctors/me/blocked-slots");
        setBlocked(new Set(r.data.blocked_slots || []));
      } catch (e) { /* silent */ }
    })();
  }, []);

  const toggle = async (slot_at) => {
    if (busy) return;
    setBusy(slot_at);
    const wasBlocked = blocked.has(slot_at);
    try {
      const r = await api.put("/doctors/me/blocked-slots",
        wasBlocked ? { remove: [slot_at] } : { add: [slot_at] });
      setBlocked(new Set(r.data.blocked_slots || []));
      toast.success(wasBlocked ? "Slot re-opened for patients" : "Slot blocked — patients won't see it");
    } catch (e) {
      toast.error("Could not update blocked slots");
    } finally { setBusy(null); }
  };

  const timeLabel = (hh) => (hh === 10 ? "10:00 AM" : hh === 14 ? "02:00 PM" : "06:00 PM");

  return (
    <Card className="p-6 border-slate-200" data-testid="doctor-slot-blocker">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-heading font-semibold text-slate-900 text-lg">Video slot blocking</div>
          <p className="text-xs text-slate-500 mt-1">Tap any slot to mark it unavailable — patients won&apos;t see it on their slot picker.</p>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200">
          {blocked.size} blocked
        </span>
      </div>
      <div className="mt-4 space-y-3">
        {slots.map((d) => (
          <div key={d.day}>
            <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{d.day}</div>
            <div className="grid grid-cols-3 gap-2">
              {d.list.map((s) => {
                const on = blocked.has(s.slot_at);
                const isBusy = busy === s.slot_at;
                return (
                  <button
                    key={s.slot_at}
                    type="button"
                    onClick={() => toggle(s.slot_at)}
                    disabled={isBusy}
                    data-testid={`block-slot-${s.slot_at}`}
                    className={
                      "h-9 rounded-lg border text-xs font-semibold transition inline-flex items-center justify-center gap-1 " +
                      (on
                        ? "bg-rose-100 border-rose-300 text-rose-800 line-through"
                        : "bg-white border-slate-200 text-slate-800 hover:border-slate-300")
                    }
                  >
                    {isBusy ? "…" : timeLabel(s.hh)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}



